/**
 * 批量 AI 审查（波次 2 契约）。一批 = 一次多选发起的文档集合审查，
 * 批内逐份文档一个 item，行级三态：排队 / 审查中 / 完成（成功、失败、跳过）。
 *
 * 契约端点：
 *   POST /documents/ai-review-batches {documentIds[], stance?} → 202 {id}
 *   GET  /ai-review-batches/{id}
 *   GET  /ai-review-batches?matterId=
 *   POST /ai-review-batches/{id}/items/{itemId}/retry → 202 新 item id
 */

/** 批级生命周期。partial_failed = 有失败行但非全部失败。 */
export type AiReviewBatchStatus =
  | "pending"
  | "running"
  | "completed"
  | "partial_failed"
  | "failed";

/** 行级（单份文档）状态，契约冻结。 */
export type AiReviewBatchItemStatus =
  | "pending"
  | "running"
  | "succeeded"
  | "failed"
  | "skipped";

/** Chinese display labels for batch statuses. */
export const AI_REVIEW_BATCH_STATUS_LABELS: Record<AiReviewBatchStatus, string> = {
  pending: "排队中",
  running: "执行中",
  completed: "已完成",
  partial_failed: "部分失败",
  failed: "失败",
};

/** Selectable batch status options. */
export const AI_REVIEW_BATCH_STATUS_OPTIONS: { value: AiReviewBatchStatus; label: string }[] = (
  Object.entries(AI_REVIEW_BATCH_STATUS_LABELS) as [AiReviewBatchStatus, string][]
).map(([value, label]) => ({ value, label }));

/** Chinese display labels for batch item statuses. */
export const AI_REVIEW_BATCH_ITEM_STATUS_LABELS: Record<AiReviewBatchItemStatus, string> = {
  pending: "排队中",
  running: "审查中",
  succeeded: "已完成",
  failed: "失败",
  skipped: "已跳过",
};

/** Selectable batch item status options. */
export const AI_REVIEW_BATCH_ITEM_STATUS_OPTIONS: {
  value: AiReviewBatchItemStatus;
  label: string;
}[] = (
  Object.entries(AI_REVIEW_BATCH_ITEM_STATUS_LABELS) as [AiReviewBatchItemStatus, string][]
).map(([value, label]) => ({ value, label }));

/** 一份文档在本批中的执行行。 */
export interface AiReviewBatchItem {
  id: string;
  documentId: string;
  documentName: string;
  status: AiReviewBatchItemStatus;
  /** 行级失败原因（status=failed 时展示）。 */
  failureReason: string | null;
  /** 该份文档本次审查产出的发现数。 */
  findingCount: number | null;
  /** 本行是重试行时，指向被重试的原行 id。 */
  retryOfId: string | null;
  /** 行级可重试标记——仅 failed 且 canRetry 的行重试按钮可用。 */
  canRetry: boolean;
}

/** 一次批量审查（详情与列表共用；列表行 items 可缺省）。 */
export interface AiReviewBatch {
  id: string;
  title: string | null;
  status: AiReviewBatchStatus;
  documentIds: string[];
  /** 审查立场（自由文本，透传 doc-review skill 的 stance）；null = 默认中立。 */
  stance: string | null;
  /** 详情含全量行；列表行可能只有摘要。 */
  items?: AiReviewBatchItem[];
  matterId?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}
